import { ApplicationRef, Inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { QueuesSharedService } from '@shared/services/queues-shared.service';
import { concat, interval } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

@Injectable({
  providedIn: 'root',
})
export class AppVersionUpdateService {
  constructor(
    private appRef: ApplicationRef,
    private swUpdate: SwUpdate,
    private snackbarService: MatSnackBar,
    private queuesSharedService: QueuesSharedService,
    @Inject(CbreLoggerToken) private readonly logger: CbreLogger
  ) {}

  /**
   * Verifies MIQ app version when app is stable and after six hours of it
   * being active.
   */
  public init() {
    // allow the app to stabilize first, before starting polling for updates with `interval()`.
    const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
    const everySixHours$ = interval(6 * 60 * 60 * 1000);
    const everySixHoursOnceAppIsStable$ = concat(appIsStable$, everySixHours$);

    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        this.snackbarService
          .open('A new MIQ version is available', 'UPDATE')
          .onAction()
          .subscribe(() => this.swUpdate.activateUpdate().then(() => this.postUpdateActivation()));
      });

    everySixHoursOnceAppIsStable$.subscribe(() => {
      this.swUpdate.checkForUpdate().then(() => this.logger.info('Update check completed'));
    });
  }

  private postUpdateActivation(): void {
    this.queuesSharedService.refreshQueuesStateInStorage().subscribe(() => document.location.reload());
  }
}
